import { Database, BookOpen, Loader2, Download, Archive } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useMemory, useResources, useBackups } from '@/hooks/useApi'
import { getToken } from '@/lib/auth'

async function downloadBackup(key: string, name: string) {
  const token = getToken()
  const res = await fetch(`/api/dashboard/backups/download?key=${encodeURIComponent(key)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) return
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function MemoryPanel() {
  const { data: memory, isLoading: memoryLoading } = useMemory()
  const { data: resources, isLoading: resourcesLoading } = useResources()
  const { data: backups, isLoading: backupsLoading } = useBackups()

  const kvNamespaces = memory ? Object.entries(memory.kv_namespaces) : []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            KV Namespaces
            {memoryLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {kvNamespaces.map(([name, info]) => (
              <div key={name} className="flex items-center justify-between p-3 rounded-lg glass-card">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-4 w-4 text-accent" />
                  <div>
                    <span className="text-sm font-medium text-text-primary">{name}</span>
                    <span className="text-[11px] text-text-muted block">
                      {info.error || `${info.key_count_sample ?? 0} keys sampled`}
                    </span>
                  </div>
                </div>
                <Badge variant={info.error ? 'danger' : 'default'}>
                  {info.error ? 'error' : `${info.key_count_sample ?? 0}`}
                </Badge>
              </div>
            ))}
            {kvNamespaces.length === 0 && !memoryLoading && (
              <div className="col-span-2 text-center py-4 text-sm text-text-muted">No KV namespaces found</div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            Backups
            {backupsLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {backups?.files.map(file => (
              <div key={file.key} className="flex items-center justify-between p-3 rounded-lg glass-card">
                <div className="flex items-center gap-3 min-w-0">
                  <Archive className="h-4 w-4 text-accent shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-text-primary block truncate">{file.name}</span>
                    <span className="text-[11px] text-text-muted block">
                      {file.size} bytes -- {new Date(file.uploaded).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => downloadBackup(file.key, file.name)} className="shrink-0">
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download
                </Button>
              </div>
            ))}
            {backups && backups.files.length === 0 && !backupsLoading && (
              <div className="text-center py-4 text-sm text-text-muted">No backups found</div>
            )}
          </div>
        </CardContent>
      </Card>

      {resources && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Platform Resources
              {resourcesLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="p-3 rounded-lg glass-card">
                <span className="text-xs font-semibold text-text-secondary uppercase">D1 Database</span>
                <div className="mt-1 text-sm text-text-primary">{resources.d1.name}</div>
                <div className="text-[11px] text-text-muted font-mono">{resources.d1.database_id}</div>
              </div>
              <div className="p-3 rounded-lg glass-card">
                <span className="text-xs font-semibold text-text-secondary uppercase">KV Namespaces</span>
                <div className="mt-1 text-sm text-text-primary">{resources.kv.namespace_count} namespaces</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {resources.kv.namespaces.map((ns) => (
                    <Badge key={ns.id} variant="muted" className="text-[10px]">{ns.title}</Badge>
                  ))}
                </div>
              </div>
              <div className="p-3 rounded-lg glass-card">
                <span className="text-xs font-semibold text-text-secondary uppercase">R2 Buckets</span>
                <div className="mt-1 text-sm text-text-primary">{resources.r2.bucket_count} buckets</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {resources.r2.buckets.map((b) => (
                    <Badge key={b.name} variant="muted" className="text-[10px]">{b.name}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
